import React, { useState } from 'react';
import {
  Shield,
  X,
  Play,
  CheckCircle2,
  AlertTriangle,
  Fingerprint,
  Layers,
  Database,
  Calendar,
  Sparkles,
  Award,
  BookOpen,
  ArrowRight,
  TrendingUp,
  Cpu
} from 'lucide-react';
import {
  runCrossNativeDifferentiationTest,
  CrossNativeDifferentiationReport,
  TEST_PROFILES
} from '../utils/crossNativeDifferentiationTest';
import { SPECIALIZED_METHODOLOGY_REGISTRY } from '../utils/specializedEvidenceRegistry';
import { PLAN_CONFIGS, SAMPLE_ASTROLOGERS, calculateConsultationFee } from '../utils/entitlementCommercialService';
import { Language } from '../types/kundali';

interface DifferentiationRegressionModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang?: Language;
}

export const DifferentiationRegressionModal: React.FC<DifferentiationRegressionModalProps> = ({
  isOpen,
  onClose,
  lang = 'en'
}) => {
  const isHi = lang === 'hi';
  const [activeTab, setActiveTab] = useState<'REGRESSION_TEST' | 'METHODOLOGY_REGISTRY' | 'CONSULTATION_ENTITLEMENT'>('REGRESSION_TEST');
  const [report, setReport] = useState<CrossNativeDifferentiationReport | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<string>(TEST_PROFILES[0].id);

  if (!isOpen) return null;

  const handleRunTest = () => {
    setIsRunning(true);
    setTimeout(() => {
      const res = runCrossNativeDifferentiationTest();
      setReport(res);
      setIsRunning(false);
    }, 400);
  };

  const selectedProfileResult = report?.profiles.find(p => p.profileId === selectedProfileId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-xs overflow-y-auto">
      <div className="bg-astro-bg border border-astro-border rounded-3xl max-w-5xl w-full max-h-[92vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-astro-border bg-[#FAF6ED] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-astro-primary text-white flex items-center justify-center shadow-xs">
              <Cpu className="w-5 h-5 text-astro-accent" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold font-serif text-astro-primary text-lg">
                  {isHi ? 'भविष्यवाणी विभेदन एवं वास्तुकला सत्यापन सूट' : 'Prediction Differentiation & Architecture Suite'}
                </h3>
                <span className="px-2 py-0.5 rounded-full bg-astro-accent/15 text-astro-accent font-mono text-[10px] font-bold">
                  v5.15 QA Engine
                </span>
              </div>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'क्रॉस-नेटिव विभेदन, प्रेडिक्शन फिंगरप्रिंट, विशिष्ट ज्योतिषीय रजिस्ट्री और परामर्श वास्तुकला'
                  : 'Cross-native differentiation test, prediction fingerprints, specialized methodology registry & commercial architecture'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-astro-border/60 hover:bg-astro-border text-astro-secondary-text flex items-center justify-center transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 px-5 pt-3 border-b border-astro-border bg-[#FAF6ED]/60 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setActiveTab('REGRESSION_TEST')}
            className={`px-4 py-2.5 rounded-t-xl text-xs font-bold flex items-center gap-2 transition-all border-b-2 ${
              activeTab === 'REGRESSION_TEST'
                ? 'border-astro-primary text-astro-primary bg-astro-bg'
                : 'border-transparent text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Fingerprint className="w-4 h-4" />
            <span>{isHi ? 'क्रॉस-नेटिव विभेदन टेस्ट (CAP-037)' : 'Cross-Native Differentiation Test (CAP-037)'}</span>
          </button>

          <button
            onClick={() => setActiveTab('METHODOLOGY_REGISTRY')}
            className={`px-4 py-2.5 rounded-t-xl text-xs font-bold flex items-center gap-2 transition-all border-b-2 ${
              activeTab === 'METHODOLOGY_REGISTRY'
                ? 'border-astro-primary text-astro-primary bg-astro-bg'
                : 'border-transparent text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Database className="w-4 h-4" />
            <span>{isHi ? 'विशिष्ट ज्योतिषीय रजिस्ट्री (CAP-038)' : 'Specialized Methodology Registry (CAP-038)'}</span>
          </button>

          <button
            onClick={() => setActiveTab('CONSULTATION_ENTITLEMENT')}
            className={`px-4 py-2.5 rounded-t-xl text-xs font-bold flex items-center gap-2 transition-all border-b-2 ${
              activeTab === 'CONSULTATION_ENTITLEMENT'
                ? 'border-astro-primary text-astro-primary bg-astro-bg'
                : 'border-transparent text-astro-secondary-text hover:text-astro-text'
            }`}
          >
            <Award className="w-4 h-4" />
            <span>{isHi ? 'परामर्श एवं उपभोक्ता पात्रता' : 'Consultation & Entitlement Engine'}</span>
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-6">
          {/* TAB 1: REGRESSION TEST */}
          {activeTab === 'REGRESSION_TEST' && (
            <div className="space-y-6">
              {/* Test Action Bar */}
              <div className="bg-[#FAF6ED] border border-astro-border rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h4 className="font-bold text-astro-primary text-sm">
                    {isHi ? 'क्रॉस-नेटिव भविष्यवाणी विभेदन परीक्षण' : 'Automated Cross-Native Prediction Differentiation Test'}
                  </h4>
                  <p className="text-xs text-astro-secondary-text">
                    {isHi
                      ? '5 भिन्न-भिन्न ज्योतिषीय प्रोफाइल पर 9 जीवन क्षेत्रों में साक्ष्य-संचालित भिन्नता और फिंगरप्रिंट का परीक्षण करता है।'
                      : 'Executes synthesis across 5 materially different native archetypes across all 9 life areas to prove evidence-driven divergence.'}
                  </p>
                </div>

                <button
                  onClick={handleRunTest}
                  disabled={isRunning}
                  className="px-5 py-2.5 rounded-xl bg-astro-primary hover:bg-[#133E3D] text-white font-bold text-xs flex items-center gap-2 shadow-xs transition-all active:scale-95 disabled:opacity-50"
                >
                  <Play className={`w-4 h-4 ${isRunning ? 'animate-spin' : 'text-astro-accent'}`} />
                  <span>{isRunning ? (isHi ? 'परीक्षण जारी है...' : 'Running Verification...') : (isHi ? 'टेस्ट चलाएं' : 'Run Full Regression Test')}</span>
                </button>
              </div>

              {report && (
                <div className="space-y-5 animate-in fade-in duration-300">
                  {/* Status Banner */}
                  <div className={`p-4 rounded-2xl border flex items-center gap-3 ${
                    report.overallStatus === 'PASS'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                      : 'bg-rose-50 border-rose-200 text-rose-900'
                  }`}>
                    {report.overallStatus === 'PASS' ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
                    )}
                    <div className="text-xs">
                      <div className="font-bold text-sm">
                        {report.overallStatus === 'PASS' ? 'REGRESSION STATUS: PASSED' : 'REGRESSION STATUS: FAILED'}
                      </div>
                      <p>{report.summary}</p>
                    </div>
                  </div>

                  {/* Pairwise Divergence Matrix */}
                  <div className="bg-astro-bg border border-astro-border rounded-2xl p-4 space-y-3">
                    <h5 className="font-bold text-xs uppercase tracking-wider text-astro-primary">
                      {isHi ? 'जोड़ीदार भिन्नता स्कोर मैट्रिक्स (Pairwise Divergence Matrix)' : 'Pairwise Divergence Score Matrix'}
                    </h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      {report.pairwiseDivergenceMatrix.map((pair, idx) => (
                        <div key={idx} className="p-2.5 rounded-xl bg-[#FAF6ED] border border-astro-border flex items-center justify-between">
                          <div className="min-w-0 pr-2">
                            <div className="font-bold text-astro-primary truncate">{pair.pair[0]} ⇄ {pair.pair[1]}</div>
                            <div className="text-[11px] text-astro-secondary-text">
                              Lagna: {pair.lagnaDifferent ? '✓ Diff' : 'Identical'} | Moon: {pair.moonSignDifferent ? '✓ Diff' : 'Identical'}
                            </div>
                          </div>
                          <span className="px-2 py-1 rounded-lg bg-emerald-100 text-emerald-800 font-bold font-mono text-xs">
                            {pair.divergenceScore}% Divergent
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Profile Deep Inspection */}
                  <div className="bg-astro-bg border border-astro-border rounded-2xl p-4 space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <h5 className="font-bold text-sm text-astro-primary">
                        {isHi ? 'व्यक्तिगत कुंडली फिंगरप्रिंट एवं साक्ष्य निरीक्षण' : 'Individual Prediction Fingerprint Inspector'}
                      </h5>
                      <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
                        {TEST_PROFILES.map(p => (
                          <button
                            key={p.id}
                            onClick={() => setSelectedProfileId(p.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                              selectedProfileId === p.id
                                ? 'bg-astro-primary text-white shadow-2xs'
                                : 'bg-[#FAF6ED] text-astro-secondary-text hover:bg-astro-border'
                            }`}
                          >
                            {p.archetype.replace('_', ' ')}
                          </button>
                        ))}
                      </div>
                    </div>

                    {selectedProfileResult && (
                      <div className="space-y-4 pt-2">
                        {/* Fingerprint Card */}
                        <div className="p-4 rounded-xl bg-[#FAF6ED] border border-astro-accent/40 space-y-3">
                          <div className="flex items-center justify-between border-b border-astro-border pb-2">
                            <div>
                              <span className="font-bold text-astro-primary text-sm">{selectedProfileResult.name}</span>
                              <div className="text-xs text-astro-secondary-text">{selectedProfileResult.archetype}</div>
                            </div>
                            <span className="px-2.5 py-1 rounded-full bg-astro-primary text-white font-mono text-xs font-bold">
                              {selectedProfileResult.fingerprint.fingerprintHash}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                            <div>
                              <span className="text-astro-secondary-text block">Lagna:</span>
                              <strong className="text-astro-primary">{selectedProfileResult.fingerprint.lagna.sign} ({selectedProfileResult.fingerprint.lagna.lord})</strong>
                            </div>
                            <div>
                              <span className="text-astro-secondary-text block">Moon:</span>
                              <strong className="text-astro-primary">{selectedProfileResult.fingerprint.moon.sign} / {selectedProfileResult.fingerprint.moon.nakshatra} (P{selectedProfileResult.fingerprint.moon.pada})</strong>
                            </div>
                            <div>
                              <span className="text-astro-secondary-text block">Dominant Engine:</span>
                              <strong className="text-emerald-700">{selectedProfileResult.fingerprint.dominantPlanetaryEngine.strengthLabel}</strong>
                            </div>
                            <div>
                              <span className="text-astro-secondary-text block">Indu Lagna:</span>
                              <strong className="text-astro-accent">{selectedProfileResult.fingerprint.dhanaIndicators.induLagnaSign} (Hora: {selectedProfileResult.fingerprint.dhanaIndicators.dominantHora})</strong>
                            </div>
                          </div>
                        </div>

                        {/* Domain Synthesis Evidence Samples */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {['career', 'wealth', 'marriage', 'health'].map(areaKey => {
                            const matrix = selectedProfileResult.lifeAreaMatrices[areaKey];
                            if (!matrix) return null;
                            return (
                              <div key={areaKey} className="p-3 rounded-xl bg-white border border-astro-border space-y-1.5">
                                <div className="flex items-center justify-between text-xs font-bold text-astro-primary">
                                  <span>{matrix.titleEn}</span>
                                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-astro-accent/15 text-astro-accent">
                                    {matrix.confidenceScore}% Confidence
                                  </span>
                                </div>
                                <p className="text-xs text-astro-text font-medium leading-relaxed">
                                  {matrix.synthesisVerdictEn}
                                </p>
                                <div className="text-[11px] text-astro-secondary-text italic">
                                  {matrix.qualificationNarrativeEn}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: METHODOLOGY REGISTRY */}
          {activeTab === 'METHODOLOGY_REGISTRY' && (
            <div className="space-y-4">
              <div className="bg-[#FAF6ED] border border-astro-border rounded-2xl p-4">
                <h4 className="font-bold text-astro-primary text-sm">
                  {isHi ? 'विशिष्ट ज्योतिषीय कार्यप्रणाली रजिस्ट्री (CAP-038)' : 'Specialized Jyotish Methodology Registry (CAP-038)'}
                </h4>
                <p className="text-xs text-astro-secondary-text">
                  {isHi
                    ? 'पाराशरी, वर्गीय कुंडलियां (D1-D60), विशेष लग्न, 22वां द्रेष्काण, 64वां नवांश, गोचर व केपी प्रणाली की आधिकारिक तकनीकी सूची।'
                    : 'Governs all registered astrological techniques, calculation statuses, synthesis eligibility, and user exposure levels.'}
                </p>
              </div>

              <div className="border border-astro-border rounded-2xl overflow-hidden bg-astro-bg">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#FAF6ED] border-b border-astro-border text-astro-primary font-bold">
                      <th className="p-3">ID & Name</th>
                      <th className="p-3">Category</th>
                      <th className="p-3">Calculation Status</th>
                      <th className="p-3">Synthesis Eligibility</th>
                      <th className="p-3">Classical Reference</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-astro-border">
                    {SPECIALIZED_METHODOLOGY_REGISTRY.map(m => (
                      <tr key={m.id} className="hover:bg-[#FAF6ED]/40 transition-colors">
                        <td className="p-3 font-bold text-astro-primary">
                          <div>{m.name}</div>
                          <span className="font-mono text-[10px] text-astro-secondary-text">{m.id}</span>
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded-full bg-astro-primary/10 text-astro-primary font-mono text-[10px] font-bold">
                            {m.category}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold font-mono ${
                            m.calculationStatus === 'FROZEN_ACTIVE'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-astro-secondary/15 text-astro-secondary'
                          }`}>
                            {m.calculationStatus}
                          </span>
                        </td>
                        <td className="p-3 font-semibold">
                          {m.synthesisEligibility ? (
                            <span className="text-emerald-700 font-bold">✓ Approved</span>
                          ) : (
                            <span className="text-astro-secondary-text italic">Isolated Layer (KP)</span>
                          )}
                        </td>
                        <td className="p-3 text-astro-secondary-text italic text-[11px]">{m.classicalReference}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 3: CONSULTATION & ENTITLEMENT */}
          {activeTab === 'CONSULTATION_ENTITLEMENT' && (
            <div className="space-y-6">
              {/* Entitlement Plans */}
              <div className="space-y-3">
                <h4 className="font-bold text-astro-primary text-sm">
                  {isHi ? 'उपभोक्ता पात्रता एवं योजना वास्तुकला' : 'Consumer Plan & Entitlement Architecture'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Object.values(PLAN_CONFIGS).slice(0, 3).map(p => (
                    <div key={p.planId} className="p-4 rounded-2xl bg-[#FAF6ED] border border-astro-border space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-astro-primary text-sm">{p.displayName}</span>
                        <span className="font-mono text-xs font-bold text-astro-accent">₹{p.basePriceInr}</span>
                      </div>
                      <div className="text-xs text-astro-secondary-text space-y-1">
                        <div>AI Quota: <strong>{p.aiQuestionsPerHour}/hr</strong></div>
                        <div>Vault Storage: <strong>{p.maxStoredKundalis} Kundalis</strong></div>
                        <div>Astrologer Core View: <strong>{p.canAccessAstrologerCore ? 'Enabled' : 'Restricted'}</strong></div>
                        <div>D1–D60 Vargas: <strong>{p.canAccessD1ToD60Charts ? 'Full Access' : 'Standard D1/D9'}</strong></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Consultation Booking Architecture */}
              <div className="space-y-3">
                <h4 className="font-bold text-astro-primary text-sm">
                  {isHi ? 'सशुल्क ज्योतिषी परामर्श सेवा संरचना' : 'Paid Astrologer Consultation Architecture'}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {SAMPLE_ASTROLOGERS.map(astro => {
                    const fee = calculateConsultationFee(astro.baseFee30MinInr, 'BASIC');
                    return (
                      <div key={astro.id} className="p-4 rounded-2xl bg-white border border-astro-border space-y-2">
                        <div className="font-bold text-astro-primary text-sm">{astro.name}</div>
                        <div className="text-xs text-astro-secondary-text">{astro.title}</div>
                        <div className="text-[11px] text-astro-text">
                          Exp: {astro.experienceYears} Years | Rating: ★ {astro.rating} ({astro.reviewCount})
                        </div>
                        <div className="pt-2 border-t border-astro-border flex items-center justify-between text-xs">
                          <span className="text-astro-secondary-text">30-Min Consultation:</span>
                          <strong className="text-astro-accent font-mono">₹{fee.finalFeeInr}</strong>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
